import dayjs, { Dayjs } from "dayjs";
import type { Simplify } from "type-fest";
import { z } from "zod";

import { Action, ACTION_STATUS, SCOPES } from "../../features/action-plan/domain/action-plan";

export const DayjsToIsoString = z
  .custom<Dayjs | string>((val) => {
    return dayjs.isDayjs(val) || (typeof val === "string" && !isNaN(Date.parse(val)));
  })
  .transform((val) => (dayjs.isDayjs(val) ? val.toISOString() : val));
export const IsoStringToDayjs = z.iso.datetime({ offset: true }).transform((val) => dayjs(val));
export const emptyObjectToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  schema.transform((val) => {
    if (val && typeof val === "object" && Object.values(val).every((v) => v === undefined)) {
      return undefined;
    }
    return val;
  });
// Antd Forms set cleared values to null
export const nullableNumber = z
  .number()
  .nullable()
  .optional()
  .transform((val) => (val === null ? undefined : val));
export const StaticCostEfficiencySchema = z.object({
  cost: nullableNumber,
  savingsPerYear: nullableNumber,
});

export const ReductionSchema = emptyObjectToUndefined(
  z.object({
    delta: nullableNumber,
    actual: nullableNumber,
    target: nullableNumber,
  })
);

export const ValeriYearResultSchema = z.object({
  capitalValue: z.number(),
  cashValue: z.number(),
  electricitySavings: z.number(),
  expenditures: z.number(),
  generalSavings: z.number(),
  naturalGasSavings: z.number(),
  onetimeCashInflow: z.number(),
  period: z.number(),
  totalSavings: z.number(),
});

export const ValeriResultScenarioSchema = z.object({
  amortizationYear: z.number().optional(),
  breakEvenRatio: z.number().optional(),
  capitalValueEndOfLifetime: z.number(),
  yearResults: ValeriYearResultSchema.array(),
});

export const ValeriResultSchema = z.object({
  mostLikelyScenario: ValeriResultScenarioSchema,
  bestCaseScenario: ValeriResultScenarioSchema,
  worstCaseScenario: ValeriResultScenarioSchema,
});

export const ValeriScenarioSchema = z.object({
  equityShare: nullableNumber,
  equityInterestRate: nullableNumber,
  debtInterestRate: nullableNumber,
  plannedEconomicLifetime: nullableNumber,
  currentElectricityPrice: nullableNumber,
  annualElectricityPriceEscalation: nullableNumber,
  currentNaturalGasPrice: nullableNumber,
  annualNaturalGasPriceEscalation: nullableNumber,
  inflation: nullableNumber,
  expenditures: nullableNumber,
  annualElectricitySavings: nullableNumber,
  annualNaturalGasSavings: nullableNumber,
  annualOperationalSavings: nullableNumber,
  onetimeCashInflows: nullableNumber,
});

export const ValeriSchema = z.object({
  mostLikelyScenario: ValeriScenarioSchema,
  bestCaseScenario: ValeriScenarioSchema,
  worstCaseScenario: ValeriScenarioSchema,
});

export const ActionBaseSchema = z.object({
  id: z.string(),
  status: z.enum(ACTION_STATUS),
  title: z.string().optional(),
  isInReview: z.boolean(),
  comment: z.string().optional(),
  assignee: z.string().optional(),
  scope: z.enum(SCOPES).optional(),
  emissionReduction: ReductionSchema.optional(),
  energyReduction: ReductionSchema.optional(),
  currency: z.string().optional(),
  staticCostEfficiency: StaticCostEfficiencySchema.optional(),
  valeri: ValeriSchema.optional(),
});

export const ActionResponseSchema = ActionBaseSchema.extend({
  startDate: IsoStringToDayjs.optional(),
  endDate: IsoStringToDayjs.optional(),
  documents: z.array(z.string()),
  valeriResult: ValeriResultSchema.optional(),
}).transform((obj) => {
  return new Action(obj.id, obj.status, {
    title: obj.title,
    isInReview: obj.isInReview,
    comment: obj.comment,
    assignee: obj.assignee,
    startDate: obj.startDate,
    endDate: obj.endDate,
    scope: obj.scope,
    emissionReduction: obj.emissionReduction,
    energyReduction: obj.energyReduction,
    staticCostEfficiency: obj.staticCostEfficiency,
    valeri: obj.valeri,
    valeriResult: obj.valeriResult,
    documents: obj.documents,
  });
});

export const ActionPUTRequestSchema = ActionBaseSchema.extend({
  startDate: DayjsToIsoString.optional(),
  endDate: DayjsToIsoString.optional(),
});

export type ActionDTO = Simplify<z.input<typeof ActionPUTRequestSchema>>;

export function isValidActionDTO(obj: any): obj is ActionDTO {
  const parseResult = ActionPUTRequestSchema.safeParse(obj);
  if (!parseResult.success) {
    console.error("ActionDTO validation errors:", parseResult.error.message);
  }
  return parseResult.success;
}

export const UploadDocumentResponseSchema = z.object({
  uploadUrl: z.string(),
});

export const UploadedDocumentResponseSchema = z.object({
  fileName: z.string(),
  downloadUrl: z.string(),
});

export type UploadedDocument = z.infer<typeof UploadedDocumentResponseSchema>;
